import { Component, OnInit, Inject, ViewChild, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { select } from '@angular-redux/store';
import { Observable, throwError } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { BreadcrumbService } from 'angular-crumbs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { catchError, tap, first, mergeMap } from 'rxjs/operators';
import { User } from 'src/app/core/users/users.model';
import { UserActionService } from 'src/app/core/users/users.action';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SuccessRequestComponent } from 'src/app/shared/success-request/success-request.component';
import { FailRequestComponent } from 'src/app/shared/fail-request/fail-request.component';
import { GeoPos } from 'src/app/core/geographicalpos/geographicalpos.model';

export interface DialogData {
  id: any;
  name: string;
}

@Component({
  selector: 'app-utente-singolo',
  templateUrl: './utente-singolo.component.html',
  styleUrls: ['./utente-singolo.component.scss']
})
export class UtenteSingoloComponent implements OnInit, OnDestroy {
  @ViewChild('authSelection') auth_selection: any;
  @ViewChild('geoposSelection') geopos_selection: any;

  @select(['logged', 'logged'])
  user_logged$: Observable<any>;
  user_logged: User;
  @select(['user', 'current'])
  user_current$: Observable<User>;
  user_current: User;
  @select(['geopos', 'list'])
  geopos_list$: Observable<any>;
  geopos_list: GeoPos[] = [];

  current_lang = 'it';
  admin_auth: string[] = ['admin', 'editor', 'blocked'];
  superadmin_auth: string[] = ['superadmin', 'admin', 'editor', 'blocked'];
  auth: string[];
  current_id: any;
  current_name: string;

  loading = false;

  constructor(private titleService: Title,
              private route: Router,
              private activateroute: ActivatedRoute,
              private apiservice: ApiService,
              private userAction: UserActionService,
              private breadcrumbservice: BreadcrumbService,
              private translate: TranslateService,
              public dialog: MatDialog) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.admin_single_user').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeLangFunction();
    });
  }

  ngOnInit(): void {
    this.user_current$.pipe(
      first(),
      mergeMap((user) => {
        this.user_current = user;
        return this.user_logged$.pipe(first());
      }),
      tap((user) => {
        this.user_logged = user;
        if (this.user_logged.auth_user === 'superadmin') {
          this.auth = this.superadmin_auth;
        } else if (this.user_logged.auth_user === 'admin' && this.user_logged.geopos[0].id === this.user_current.geopos[0].id) {
          this.auth = this.admin_auth;
        } else {
          this.routerFunction('/');
        }
        this.current_id = this.user_current.id;
        this.current_name = this.user_current.name;
        this.changeLangFunction();
      })
    ).subscribe();

    this.geopos_list$.pipe(
      first(),
      tap((list) => {
        this.geopos_list = [...list];
      })
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.userAction.setUserCurrent(null);
  }

  checkGeopos(geopos) {
    if (this.user_current.geopos.length > 0) {
      return this.user_current.geopos.some(({id}) => id === geopos.id);
    } else {
      return false;
    }
  }

  geoposSelectionFunction() {
    this.user_current.geopos = this.geopos_selection.selectedOptions.selected.map((opt) => opt._value);
  }

  elemDelete() {
    this.dialog.open(DeleteUserDialogContent, {
      width: '450px',
      data: {
        id: this.current_id,
        name: this.current_name
      }
    });
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.titleService.setTitle(this.user_current.name + ' - PRIMIS');
    this.breadcrumbservice.changeBreadcrumb(this.activateroute.snapshot, this.user_current.name);
  }

  saveUser() {
    this.loading = true;
    this.apiservice.modifyUser(this.user_current).pipe(
      tap((x: any) => {
        if (x && !x.error) {
          this.userAction.addOrUpdateUser(x);
          this.loading = false;
          const dialogRef = this.dialog.open(SuccessRequestComponent, {
            width: '450px'
          });
          dialogRef.afterClosed().subscribe(() => this.routerFunction('/lista-utenti'));
        }
      }),
      catchError(error => {
        this.loading = false;
        this.dialog.open(FailRequestComponent, {
          width: '450px'
        });
        return throwError(error);
      })
    ).subscribe();
  }

  authSelectionFunction() {
    const isSuperAdmin = this.user_current.auth_user === 'superadmin';
    this.user_current.isadmin = isSuperAdmin;
    if (isSuperAdmin) {
      this.user_current.geopos = [...this.geopos_list];
    } else if (this.user_logged.auth_user === 'superadmin') {
      this.user_current.geopos = [];
    } else {
      this.user_current.geopos = [...this.user_logged.geopos];
    }
  }

  routerFunction(path_to_new_page) {
    this.route.navigate([path_to_new_page]);
  }

  saveButtonDisabled() {
    const isValid =
      !!this.user_current.name && !!this.user_current.auth_user &&
      this.user_current.geopos.length > 0;
    return !isValid;
  }
}

@Component({
  selector: 'deleteuser-dialog-content',
  templateUrl: 'deleteuser-dialog-content.html',
  styleUrls: ['./utente-singolo.component.scss']
})
export class DeleteUserDialogContent {
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<DeleteUserDialogContent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private apiservice: ApiService,
    private route: Router,
    public dialog: MatDialog,
    private userAction: UserActionService) {}

  onNoClick(): void {
    this.dialogRef.close();
  }

  deleteUser() {
    this.loading = true;
    let user_id = this.data.id;
    this.apiservice.deleteUser(user_id).pipe(
      tap((x: any) => {
        this.userAction.deleteUser(user_id);
        this.loading = false;
        this.dialogRef.close();
        if (x && !x.error) {
          const dialogRef = this.dialog.open(SuccessRequestComponent, {
            width: '450px'
          });
          dialogRef.afterClosed().subscribe(() => this.routerFunction('/lista-utenti'));
        }
      }),
      catchError(error => {
        this.loading = false;
        this.dialog.open(FailRequestComponent, {
          width: '450px'
        });
        return throwError(error);
      })
    ).subscribe();
  }

  routerFunction(path_to_new_page) {
    this.route.navigate([path_to_new_page]);
  }
}
